import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Award, Lightbulb, Users, Plus, X, Save, FileText, FileDown, Loader2,
} from "lucide-react";
import { KNOWN_TEAM } from "./projectUtils";

export function ProjectCierreDialog({
  open, onOpenChange, project, form, setForm,
  newKpi, setNewKpi, newLeccion, setNewLeccion,
  newPatente, setNewPatente, newMiembro, setNewMiembro,
  onSave, onExport, loading,
}) {
  const addKpi = () => {
    if (!newKpi.nombre) return;
    setForm({ ...form, kpis_resultados: [...form.kpis_resultados, newKpi] });
    setNewKpi({ nombre: "", valor: "" });
  };

  const addLeccion = () => {
    if (!newLeccion.trim()) return;
    setForm({ ...form, lecciones_aprendidas: [...form.lecciones_aprendidas, newLeccion.trim()] });
    setNewLeccion("");
  };

  const addPatente = () => {
    if (!newPatente.titulo) return;
    setForm({ ...form, patentes: [...form.patentes, newPatente] });
    setNewPatente({ titulo: "", descripcion: "", tipo: "Patente", estado: "En trámite" });
  };

  const addMiembro = () => {
    if (!newMiembro.trim()) return;
    setForm({ ...form, equipo: [...form.equipo, newMiembro.trim()] });
    setNewMiembro("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Ficha de Cierre — {project?.project_name}
          </DialogTitle>
          <DialogDescription>
            Documenta el cierre del proyecto. Guarda primero y luego exporta en PDF o DOCX.
          </DialogDescription>
        </DialogHeader>

        {loading && !form.resumen_ejecutivo ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="resumen">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="resumen">Resumen</TabsTrigger>
              <TabsTrigger value="resultados">Resultados</TabsTrigger>
              <TabsTrigger value="lecciones">Lecciones</TabsTrigger>
              <TabsTrigger value="ip">IP / Patentes</TabsTrigger>
              <TabsTrigger value="equipo">Equipo</TabsTrigger>
            </TabsList>

            <TabsContent value="resumen" className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Fecha de cierre</Label>
                <Input type="date" value={form.closure_date}
                  onChange={e => setForm({ ...form, closure_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Resumen ejecutivo</Label>
                <Textarea rows={4} placeholder="Describe en 2-3 párrafos el alcance, resultado y contexto del proyecto..."
                  value={form.resumen_ejecutivo}
                  onChange={e => setForm({ ...form, resumen_ejecutivo: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Objetivos alcanzados</Label>
                <Textarea rows={3} placeholder="¿Qué objetivos se cumplieron? ¿Cuáles quedaron pendientes?"
                  value={form.objetivos_alcanzados}
                  onChange={e => setForm({ ...form, objetivos_alcanzados: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Modelo de negocio / Monetización</Label>
                <Textarea rows={2} placeholder="¿Cómo se monetizó o se prevé monetizar el resultado?"
                  value={form.modelo_negocio}
                  onChange={e => setForm({ ...form, modelo_negocio: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Próximos pasos y spin-offs</Label>
                <Textarea rows={2} placeholder="Continuidad, spin-offs, transferencia tecnológica..."
                  value={form.proximos_pasos}
                  onChange={e => setForm({ ...form, proximos_pasos: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Documentación y anexos</Label>
                <Textarea rows={2} placeholder="Links a documentos, informes, memorias..."
                  value={form.documentacion}
                  onChange={e => setForm({ ...form, documentacion: e.target.value })} />
              </div>
            </TabsContent>

            <TabsContent value="resultados" className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>KPIs y Resultados Clave</Label>
                {form.kpis_resultados.map((kpi, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                    <span className="text-sm font-medium flex-1">{kpi.nombre}</span>
                    <span className="text-sm text-primary font-bold">{kpi.valor}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6"
                      onClick={() => setForm({ ...form, kpis_resultados: form.kpis_resultados.filter((_, j) => j !== i) })}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input placeholder="KPI (ej: Patentes generadas)" value={newKpi.nombre}
                    onChange={e => setNewKpi({ ...newKpi, nombre: e.target.value })} />
                  <Input placeholder="Valor (ej: 2)" value={newKpi.valor}
                    onChange={e => setNewKpi({ ...newKpi, valor: e.target.value })} className="w-32" />
                  <Button variant="outline" onClick={addKpi}><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="lecciones" className="space-y-4 pt-2">
              <Label>Lecciones Aprendidas</Label>
              {form.lecciones_aprendidas.map((l, i) => (
                <div key={i} className="flex items-start gap-2 p-3 border rounded-md bg-amber-50 dark:bg-amber-900/10 border-amber-200">
                  <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm flex-1">{l}</span>
                  <Button size="icon" variant="ghost" className="h-6 w-6"
                    onClick={() => setForm({ ...form, lecciones_aprendidas: form.lecciones_aprendidas.filter((_, j) => j !== i) })}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Textarea rows={2} placeholder="Describe una lección aprendida..." value={newLeccion}
                  onChange={e => setNewLeccion(e.target.value)} />
                <Button variant="outline" className="self-end" onClick={addLeccion}><Plus className="w-4 h-4" /></Button>
              </div>
            </TabsContent>

            <TabsContent value="ip" className="space-y-4 pt-2">
              <Label>Propiedad Intelectual y Patentes</Label>
              {form.patentes.map((p, i) => (
                <div key={i} className="p-3 border rounded-md bg-blue-50 dark:bg-blue-900/10 border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-blue-700">{p.titulo}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6"
                      onClick={() => setForm({ ...form, patentes: form.patentes.filter((_, j) => j !== i) })}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex gap-2 mt-1">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{p.tipo}</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{p.estado}</span>
                  </div>
                  {p.descripcion && <p className="text-xs text-muted-foreground mt-1">{p.descripcion}</p>}
                </div>
              ))}
              <div className="space-y-2 p-3 border rounded-md">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Título de la patente/modelo" value={newPatente.titulo}
                    onChange={e => setNewPatente({ ...newPatente, titulo: e.target.value })} />
                  <Select value={newPatente.tipo} onValueChange={v => setNewPatente({ ...newPatente, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Patente", "Modelo de utilidad", "Secreto industrial", "Know-how", "Software"].map(t =>
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Descripción breve" value={newPatente.descripcion}
                    onChange={e => setNewPatente({ ...newPatente, descripcion: e.target.value })} />
                  <Select value={newPatente.estado} onValueChange={v => setNewPatente({ ...newPatente, estado: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["En trámite", "Concedida", "En estudio", "Denegada"].map(e =>
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" className="w-full" onClick={addPatente}>
                  <Plus className="w-4 h-4 mr-2" />Añadir propiedad intelectual
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="equipo" className="space-y-4 pt-2">
              <Label>Equipo Involucrado</Label>
              <div className="flex flex-wrap gap-2">
                {form.equipo.map((m, i) => (
                  <div key={i} className="flex items-center gap-1 bg-muted px-3 py-1 rounded-full text-sm">
                    <Users className="w-3 h-3" />{m}
                    <button
                      onClick={() => setForm({ ...form, equipo: form.equipo.filter((_, j) => j !== i) })}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Nombre del miembro del equipo" value={newMiembro}
                  onChange={e => setNewMiembro(e.target.value)}
                  list="cierre-team-list"
                  onKeyDown={e => { if (e.key === "Enter") addMiembro(); }} />
                <datalist id="cierre-team-list">
                  {KNOWN_TEAM.map(n => <option key={n} value={n} />)}
                </datalist>
                <Button variant="outline" onClick={addMiembro}><Plus className="w-4 h-4" /></Button>
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 mr-auto">
            <Button variant="outline" size="sm" onClick={() => onExport("pdf")} disabled={loading}>
              <FileText className="w-4 h-4 mr-1 text-red-500" />PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => onExport("docx")} disabled={loading}>
              <FileDown className="w-4 h-4 mr-1 text-blue-500" />DOCX
            </Button>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-2" />Cerrar
          </Button>
          <Button onClick={onSave} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar ficha de cierre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
